import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Search,
  Trash2,
  Edit3,
  Tag,
  Folder,
  X,
  Save,
  RefreshCcw,
  Layers,
  Info,
  ChevronRight,
  Hash,
  List,
  LayoutGrid,
  Eye,
  EyeOff,
  CheckCircle2,
  Archive,
  FolderOpen
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Categorías / Rubros (product_categories)
 * Refactorizado al estilo Productos.jsx
 * - KPIs superiores
 * - Header unificado
 * - Tabla y Modal estilizados
 */
const Categorias = () => {
  const { profile } = useAuth();

  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI
  const [showModal, setShowModal] = useState(false);
  const [layout, setLayout] = useState('lista'); // lista | grilla
  const [viewMode, setViewMode] = useState('activos'); // activos | archivados
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');

  const fetchCategorias = useCallback(async () => {
    if (!profile?.organization_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('name', { ascending: true });

      if (error) throw error;
      setCategorias(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  // --- LÓGICA FORMULARIO ---
  const handleOpenModal = (cat = null) => {
    if (cat) {
      setEditingId(cat.id);
      setName(cat.name);
    } else {
      setEditingId(null);
      setName('');
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('El nombre es obligatorio');

    setSaving(true);
    try {
      const payload = {
        name: name.trim().toUpperCase(),
        organization_id: profile.organization_id,
        is_active: true // Por defecto activas al crear
      };

      if (editingId) {
        const { error } = await supabase
          .from('product_categories')
          .update({ name: payload.name }) // Solo actualizamos nombre, el estado se maneja aparte
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Categoría actualizada');
      } else {
        const { error } = await supabase
          .from('product_categories')
          .insert([payload]);
        if (error) throw error;
        toast.success('Categoría creada');
      }
      setShowModal(false);
      fetchCategorias();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEstado = async (cat) => {
    // Si borramos => archivamos (is_active = false)
    // Si recuperamos => activamos (is_active = true)
    // Usamos archived_at para consistencia si se requiere, pero is_active manda en la UI
    const nuevoEstado = !cat.is_active;
    try {
      const { error } = await supabase
        .from('product_categories')
        .update({ 
            is_active: nuevoEstado,
            archived_at: nuevoEstado ? null : new Date().toISOString()
        })
        .eq('id', cat.id);

      if (error) throw error;
      toast.success(nuevoEstado ? 'Categoría reactivada' : 'Categoría archivada');
      fetchCategorias();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // --- FILTROS ---
  const categoriasFiltradas = useMemo(() => {
    return categorias.filter(c => {
      // 1. Filtro de estado
      if (viewMode === 'activos' && !c.is_active) return false;
      if (viewMode === 'archivados' && c.is_active) return false;

      // 2. Buscador
      if (searchTerm && !c.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      return true;
    });
  }, [categorias, viewMode, searchTerm]);

  // KPI CALCS
  const kpis = useMemo(() => {
    const total = categorias.length;
    const activas = categorias.filter(c => c.is_active).length;
    const archivadas = total - activas;
    return { total, activas, archivadas };
  }, [categorias]);

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER ESTILO PRODUCTOS */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Layers size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Categorías y Rubros</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Organización del catálogo de productos.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* Toggle View Mode */}
          <div className="flex bg-white rounded-2xl border border-slate-200 p-1 shadow-sm">
             <button 
               onClick={() => setViewMode('activos')}
               className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'activos' ? 'bg-[#0B2A36] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
             >
                <CheckCircle2 size={14} /> Activas
             </button>
             <button 
               onClick={() => setViewMode('archivados')}
               className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'archivados' ? 'bg-slate-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
             >
                <Archive size={14} /> Archivadas
             </button>
          </div>

          <button 
            data-onboarding-id="categoria-new-btn"
            onClick={() => handleOpenModal()} 
            className="px-5 py-2.5 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border border-[#3ff2aa]"
          >
            <Plus size={16} strokeWidth={3} /> Nueva Categoría
          </button>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Rubros</p>
            <FolderOpen size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En catálogo</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Visibles POS</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.activas}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Operativas</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ocultas</p>
                <Archive size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
                {kpis.archivadas}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fuera de venta</p>
        </div>
      </div>

      {/* BARRA DE HERRAMIENTAS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
         <div className="flex flex-col md:flex-row items-center gap-3">
             
             {/* Búsqueda */}
             <div className="relative flex-1 w-full">
                 <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                    <Search size={10} /> Buscar Rubro
                 </label>
                 <div className="relative">
                    <input 
                        type="text"
                        placeholder="NOMBRE DE LA CATEGORÍA..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase text-slate-700"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                 </div>
             </div>

             {/* Layout Toggle */}
             <div className="flex items-end h-[58px]">
                <div className="flex bg-slate-50 rounded-2xl p-1 border border-slate-100 h-[38px]">
                    <button 
                        onClick={() => setLayout('lista')}
                        className={`p-2 rounded-xl transition-all ${layout === 'lista' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <List size={16} />
                    </button>
                    <button 
                        onClick={() => setLayout('grilla')}
                        className={`p-2 rounded-xl transition-all ${layout === 'grilla' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <LayoutGrid size={16} />
                    </button>
                </div>
             </div>
         </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="w-full">
        {loading ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400 gap-2">
                <RefreshCcw className="animate-spin" size={24} />
                <p className="text-[10px] font-black uppercase tracking-widest">Cargando catálogo...</p>
            </div>
        ) : categoriasFiltradas.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-100 rounded-3xl">
                <Folder size={40} strokeWidth={1} />
                <p className="text-[10px] font-black uppercase tracking-widest">No se encontraron categorías</p>
            </div>
        ) : layout === 'lista' ? (
            // VISTA LISTA
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto imp-table-wrap">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                <th className="px-5 py-3 pl-5">Nombre del Rubro</th>
                                <th className="px-4 py-3 text-right">Estado</th>
                                <th className="px-4 py-3 text-right pr-5">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50" translate="no">
                            {categoriasFiltradas.map(c => (
                                <tr key={c.id} className="hover:bg-blue-50/20 transition-all group">
                                    <td className="px-5 py-3 pl-5">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center border shadow-sm ${c.is_active ? 'bg-white border-slate-100 text-[#51A7FD]' : 'bg-slate-50 border-slate-100 text-slate-300'}`}>
                                                <Tag size={14} />
                                            </div>
                                            <span className={`text-[11px] font-black uppercase tracking-tight ${c.is_active ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                                                {c.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {c.is_active ? (
                                            <span className="inline-flex items-center px-2 py-1 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 text-[8px] font-black uppercase">
                                                Activo
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-1 rounded-lg bg-slate-100 text-slate-400 border border-slate-200 text-[8px] font-black uppercase">
                                                Archivado
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 pr-5 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleOpenModal(c)}
                                                className="p-1.5 text-slate-400 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all"
                                                title="Editar nombre"
                                            >
                                                <Edit3 size={14} />
                                            </button>
                                            <button 
                                                onClick={() => handleToggleEstado(c)}
                                                className={`p-1.5 rounded-lg transition-all ${c.is_active ? 'text-slate-400 hover:text-rose-500 hover:bg-rose-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                                                title={c.is_active ? "Archivar" : "Reactivar"}
                                            >
                                                {c.is_active ? <Trash2 size={14} /> : <RefreshCcw size={14} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        ) : (
            // VISTA GRILLA
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {categoriasFiltradas.map(c => (
                    <div key={c.id} className={`bg-white rounded-2xl border p-4 transition-all hover:shadow-lg group relative flex flex-col justify-between h-32 ${c.is_active ? 'border-slate-200' : 'border-slate-100 opacity-70 bg-slate-50'}`} translate="no">
                        <div className="flex justify-between items-start">
                            <div className={`p-2.5 rounded-xl ${c.is_active ? 'bg-blue-50 text-[#51A7FD]' : 'bg-slate-200 text-slate-400'}`}>
                                <Folder size={18} />
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => handleOpenModal(c)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#51A7FD] transition-all">
                                    <Edit3 size={14} />
                                </button>
                            </div>
                        </div>
                        
                        <div>
                            <h3 className={`text-xs font-black uppercase tracking-tight mb-1 truncate ${c.is_active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                                {c.name}
                            </h3>
                            <div className="flex justify-between items-center">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                                    ID: {c.id.slice(0,4)}
                                </span>
                                <button 
                                    onClick={() => handleToggleEstado(c)}
                                    className={`text-[9px] font-black uppercase hover:underline ${c.is_active ? 'text-rose-400' : 'text-emerald-500'}`}
                                >
                                    {c.is_active ? 'Archivar' : 'Activar'}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* MODAL FORMULARIO */}
      {showModal && (
        <div className="fixed inset-0 bg-[#0B2A36]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl border border-white/20 flex flex-col animate-in zoom-in-95 duration-200">
            
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2A36] rounded-xl flex items-center justify-center text-[#3FF2AA] shadow-sm">
                  <Tag size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">{editingId ? 'Editar Rubro' : 'Nuevo Rubro'}</h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Clasificación de productos</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 bg-white border border-slate-200 rounded-full hover:bg-slate-100 transition-all text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre de la Categoría</label>
                <div className="relative">
                  <Folder className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                  <input
                    required
                    autoFocus
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-12 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black text-slate-700 outline-none focus:border-[#51A7FD] shadow-inner uppercase"
                    placeholder="EJ: BEBIDAS, LÁCTEOS..."
                  />
                </div>
              </div>

              <div className="pt-4 flex flex-col gap-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#3FF2AA] text-[#0B2A36] py-5 rounded-[1.5rem] font-black uppercase tracking-[0.3em] text-[11px] shadow-xl shadow-emerald-500/10 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {saving ? <RefreshCcw className="animate-spin" size={18} /> : <Save size={18} />}
                  <span>{editingId ? 'Confirmar Cambios' : 'Registrar Categoría'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
};

export default Categorias;